import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HexColorPicker } from 'react-colorful';
import { AudiqMark, AudiqWordmark } from '@audiq/ui';
import { api, ApiClientError } from '../../lib/api';
import { FormRow, PrimaryButton, SettingsCard, SuccessHint, inputStyle } from './_shared';

const DEFAULT_PRIMARY = '#2A8478';
const DEFAULT_ACCENT = '#F2A93B';

const FONT_OPTIONS = [
  { value: '', label: 'DM Sans (default Audiq)' },
  { value: 'Inter', label: 'Inter' },
  { value: 'Manrope', label: 'Manrope' },
  { value: 'Plus Jakarta Sans', label: 'Plus Jakarta Sans' },
  { value: 'Outfit', label: 'Outfit' },
  { value: 'Source Sans 3', label: 'Source Sans 3' },
];

export function BrandPage() {
  const qc = useQueryClient();
  const settingsQuery = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => api.getSettings(),
  });
  const [primary, setPrimary] = useState(DEFAULT_PRIMARY);
  const [accent, setAccent] = useState(DEFAULT_ACCENT);
  const [font, setFont] = useState('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPrimaryPicker, setShowPrimaryPicker] = useState(false);
  const [showAccentPicker, setShowAccentPicker] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!settingsQuery.data) return;
    const b = settingsQuery.data.settings.branding;
    setPrimary(b.primary_color ?? DEFAULT_PRIMARY);
    setAccent(b.secondary_color ?? DEFAULT_ACCENT);
    setFont(b.font_override ?? '');
    setLogoUrl(b.logo_url);
  }, [settingsQuery.data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      api.updateBranding({
        brand_primary_color: primary,
        brand_secondary_color: accent,
        brand_font_override: font || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'settings'] });
      setShowSuccess(true);
      setError(null);
      setTimeout(() => setShowSuccess(false), 2500);
    },
    onError: (err) => setError(err instanceof ApiClientError ? err.payload.error : 'Errore'),
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => api.uploadBrandingLogo(file),
    onSuccess: (data) => {
      setLogoUrl(data.url);
      qc.invalidateQueries({ queryKey: ['admin', 'settings'] });
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Upload fallito'),
  });

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    uploadMutation.mutate(file);
  }

  return (
    <div className="space-y-6">
      <SettingsCard
        kicker="Brand"
        title="Brand cliente"
        description="Logo e colori applicati alla chat utente, ai PDF e all'admin."
        rightSlot={<SuccessHint visible={showSuccess} />}
      >
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <FormRow label="Logo" hint="PNG/JPG/SVG/WEBP · max 2 MB · 512×512 consigliato">
              <div
                className="flex items-center gap-3 rounded-md border p-3"
                style={{ borderColor: 'var(--line)', background: 'var(--soft)' }}
              >
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt="Logo cliente"
                    className="h-12 w-12 rounded-md object-contain"
                    style={{ background: '#fff' }}
                  />
                ) : (
                  <div
                    className="flex h-12 w-12 items-center justify-center rounded-md"
                    style={{ background: 'var(--paper)', color: 'var(--mute)' }}
                  >
                    no logo
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    onChange={onFile}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadMutation.isPending}
                    className="rounded-md border px-3 py-1.5 text-xs"
                    style={{ borderColor: 'var(--line)', color: 'var(--ink)' }}
                  >
                    {uploadMutation.isPending ? 'Upload…' : 'Carica nuovo'}
                  </button>
                </div>
              </div>
            </FormRow>

            <FormRow label="Colore primario" hint="Sostituisce smeraldo Audiq (#2A8478)">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowPrimaryPicker((v) => !v)}
                  className="h-10 w-10 rounded-md border"
                  style={{ background: primary, borderColor: 'var(--line)' }}
                  aria-label="Apri color picker"
                />
                <input
                  className="mono w-32 rounded-md border px-3 py-2 text-sm"
                  style={inputStyle()}
                  value={primary}
                  onChange={(e) => setPrimary(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setPrimary(DEFAULT_PRIMARY)}
                  className="text-xs underline"
                  style={{ color: 'var(--mute)' }}
                >
                  Default
                </button>
              </div>
              {showPrimaryPicker && (
                <div className="mt-2">
                  <HexColorPicker color={primary} onChange={setPrimary} />
                </div>
              )}
            </FormRow>

            <FormRow label="Colore secondario / accento" hint="Sostituisce ambra Audiq (#F2A93B)">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowAccentPicker((v) => !v)}
                  className="h-10 w-10 rounded-md border"
                  style={{ background: accent, borderColor: 'var(--line)' }}
                />
                <input
                  className="mono w-32 rounded-md border px-3 py-2 text-sm"
                  style={inputStyle()}
                  value={accent}
                  onChange={(e) => setAccent(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setAccent(DEFAULT_ACCENT)}
                  className="text-xs underline"
                  style={{ color: 'var(--mute)' }}
                >
                  Default
                </button>
              </div>
              {showAccentPicker && (
                <div className="mt-2">
                  <HexColorPicker color={accent} onChange={setAccent} />
                </div>
              )}
            </FormRow>

            <FormRow label="Font UI" hint="Font Google sostitutivo (default DM Sans)">
              <select
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={inputStyle()}
                value={font}
                onChange={(e) => setFont(e.target.value)}
              >
                {FONT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </FormRow>

            {error && (
              <div className="text-sm" style={{ color: 'var(--danger)' }}>
                {error}
              </div>
            )}

            <div className="mt-2">
              <PrimaryButton
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? 'Salvo…' : 'Salva brand'}
              </PrimaryButton>
            </div>
          </div>

          <div>
            <p className="mb-2 mono text-xs" style={{ color: 'var(--mute)' }}>
              Anteprima
            </p>
            <div
              className="rounded-lg border p-6"
              style={{
                background: '#fff',
                borderColor: 'var(--line)',
                fontFamily: font || 'DM Sans, system-ui, sans-serif',
              }}
            >
              <div className="mb-4 flex items-center gap-3">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" className="h-10 w-10 rounded-md object-contain" />
                ) : (
                  <div
                    className="rounded-full"
                    style={{
                      width: 40,
                      height: 40,
                      background: primary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontFamily: 'Outfit, sans-serif',
                      fontWeight: 700,
                    }}
                  >
                    A<span style={{ color: accent, fontSize: 28, lineHeight: 0 }}>.</span>
                  </div>
                )}
                <span
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    fontWeight: 700,
                    fontSize: 28,
                    letterSpacing: '-0.04em',
                    color: '#1f2421',
                  }}
                >
                  Audiq<span style={{ color: accent }}>.</span>
                </span>
              </div>
              <h3 style={{ fontFamily: 'DM Serif Display, serif', fontSize: 22, color: '#1f2421' }}>
                Anteprima brand cliente
              </h3>
              <p className="mt-2 text-sm" style={{ color: '#7a7269' }}>
                Caratteri, colori e logo come appariranno al paziente in chat e nei PDF report.
              </p>
              <button
                style={{
                  marginTop: 12,
                  padding: '8px 16px',
                  background: primary,
                  color: '#fff',
                  borderRadius: 4,
                  fontWeight: 600,
                }}
              >
                Pulsante primario
              </button>
            </div>
            <div className="mt-4">
              <AudiqWordmark size="md" />
              <div className="mt-2">
                <AudiqMark size={48} state="idle" />
              </div>
            </div>
          </div>
        </div>
      </SettingsCard>
    </div>
  );
}
